import React from "react"
import { isHoliday } from "feiertagejs"
import { isHolidayToday } from "utils/holidays"
import dayjs from "dayjs"

// components
import ReviewBox from "./ReviewBox"
import {
  Row,
  Space,
  Typography,
  Button,
  Divider,
  Avatar,
  message,
  Tag,
  Alert,
} from "antd"
import { ClockCircleFilled } from "@ant-design/icons"

// configs
import config from "config"

// firebase
import { Firestore } from "services/firebase"
import { doc, updateDoc } from "firebase/firestore"

// context
import { useTranslation } from "react-i18next"
import { useOrders } from "store/orders"
import { useFunnel } from "store/funnel"

const OrderDrawer = ({ orderId, onFinish }) => {
  const orders = useOrders()
  const { t } = useTranslation()

  const funnel = useFunnel()

  const [messageApi, contextHolder] = message.useMessage()

  const [state, setState] = React.useState("idle")

  const holidayToday = React.useMemo(() => {
    return isHolidayToday()
  }, [])

  return (
    <div>
      {contextHolder}

      <Row justify="start">
        <Button
          type="primary"
          onClick={() => {
            onFinish()
          }}
        >
          {t("finish")}
        </Button>
      </Row>

      <Divider />

      {(() => {
        const currentOrderData = orders.orders.find((o) => o.id === orderId)

        switch (currentOrderData?.status) {
          case "created":
            return (
              <>
                <Avatar
                  src="icon-process/waiting.gif"
                  size={40}
                  alt="icon-process-waiting"
                />

                <Typography.Paragraph>{t("wait_moment")}</Typography.Paragraph>

                <Typography.Paragraph>
                  {t("order_received")}
                </Typography.Paragraph>

                <Button
                  danger
                  loading={state === "storno-order" ? true : undefined}
                  type="primary"
                  onClick={async () => {
                    try {
                      setState("storno-order")

                      await updateDoc(doc(Firestore, "orders", orderId), {
                        status: "cancelled-by-user",
                      })

                      funnel.add("order_cancel")

                      setState("idle")
                    } catch (e) {
                      messageApi.error(e.message)
                      console.error(e)
                      setState("idle")
                    }
                  }}
                >
                  {t("cancel_order")}
                </Button>

                <Divider />

                <Typography.Paragraph type="secondary">
                  {t("order_note")}
                </Typography.Paragraph>

                <Typography.Paragraph>
                  {holidayToday ? (
                    <Tag icon={<ClockCircleFilled />}>
                      <Space>
                        <Typography.Text strong>
                          {t("today_colon")}
                        </Typography.Text>
                        <Typography.Text strong>{t("holiday")}</Typography.Text>
                      </Space>
                    </Tag>
                  ) : (
                    <Tag icon={<ClockCircleFilled />}>
                      <Space>
                        <Typography.Text strong>{t("today")}</Typography.Text>
                        {config.hours[new Date().getDay()] ? (
                          <span>
                            {config.hours[new Date().getDay()].map(
                              ({ start_time, end_time }) => (
                                <Tag key={dayjs(start_time).format("HH:mm")}>
                                  {`${dayjs(start_time).format(
                                    "HH:mm"
                                  )} - ${dayjs(end_time).format("HH:mm")}`}
                                </Tag>
                              )
                            )}
                          </span>
                        ) : (
                          t("closed")
                        )}
                      </Space>
                    </Tag>
                  )}
                </Typography.Paragraph>
              </>
            )

          case "accepted":
            return (
              <>
                <Avatar
                  src="icon-process/working.gif"
                  size={40}
                  alt="icon-process-working"
                />

                <Typography.Paragraph>
                  {t("thank_you_caps")}
                </Typography.Paragraph>
                <Typography.Paragraph strong>
                  {t("we_received_order")}
                </Typography.Paragraph>

                {currentOrderData?.eta && (
                  <Alert
                    message={
                      <Typography.Paragraph strong style={{ marginBottom: 0 }}>
                        <Space direction="vertical">
                          <Typography.Text>
                            {t("estimated_time")}
                          </Typography.Text>
                          <Typography.Text strong style={{ fontSize: "25px" }}>
                            <ClockCircleFilled /> {currentOrderData?.eta}{" "}
                            {t("minutes")}
                          </Typography.Text>
                        </Space>
                      </Typography.Paragraph>
                    }
                    type="warning"
                    style={{ marginBottom: 16 }}
                  />
                )}

                <Typography.Paragraph>
                  {t("order_accepted")}
                </Typography.Paragraph>

                <Divider />

                <ReviewBox />
              </>
            )

          case "finished":
            return (
              <>
                {currentOrderData.type !== "delivery" ? (
                  <Avatar
                    src="icon-process/ready.png"
                    size={40}
                    alt="icon-process-ready"
                  />
                ) : (
                  <Avatar
                    src="icon-process/truck.png"
                    size={40}
                    alt="icon-process-truck"
                  />
                )}

                <Typography.Paragraph>
                  <strong>{t("finished_caps")}</strong>
                </Typography.Paragraph>
                <Typography.Paragraph>
                  {t("order_finished_text")}
                </Typography.Paragraph>
                <Typography.Paragraph>
                  {t("good_appetite")}
                </Typography.Paragraph>
                <Typography.Paragraph>{config.title}</Typography.Paragraph>
              </>
            )

          case "refused":
            return (
              <>
                <Avatar
                  src="icon-process/refuse.png"
                  size={40}
                  alt="icon-process-refuse"
                />

                <Typography.Paragraph>
                  {t("order_refused_text")}
                  <br />
                  {t("please_contact_phone")}
                </Typography.Paragraph>
                <Typography.Paragraph>
                  {config.phones.join(", ")}
                </Typography.Paragraph>
                <Typography.Paragraph>{t("thank_you")}</Typography.Paragraph>
              </>
            )

          case "cancelled-by-user":
            return (
              <>
                <Avatar
                  src="icon-process/refuse.png"
                  size={40}
                  alt="icon-process-refuse"
                />

                <Typography.Paragraph>
                  {t("order_cancelled")}
                </Typography.Paragraph>
              </>
            )

          default:
            return "undefined-status"
        }
      })()}
    </div>
  )
}

export default OrderDrawer
